import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Button, message } from 'antd';
import { createWaterAnalysis, getLastWaterAnalysis } from '../../../Utils/analysis/waterAnalysis';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../../Shared/Header';
import backIcon from "../../../assets/icons/backIcon.svg";
import Loading from '../../Shared/Loading';
import CustomDatePicker from '../../Shared/CustomDatePicker';

const CreateWaterAnalysis = () => {
    const [form] = Form.useForm();
    const { exploitationId } = useParams();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [lastAnalysis, setLastAnalysis] = useState(null);

    useEffect(() => {
        const fetchLastAnalysis = async () => {
            try {
                const lastAnalysis = await getLastWaterAnalysis(exploitationId);
                if (lastAnalysis) {
                    setLastAnalysis(lastAnalysis);
                    const prefillValues = {};
                    waterParameters.forEach(param => {
                        if (!param.isHeader) {
                            prefillValues[`${param.key}_limval`] = lastAnalysis[`${param.key}_limval`];
                        }
                    });
                    form.setFieldsValue(prefillValues);
                }
            } catch (error) {
                console.error('Error fetching last water analysis:', error);
            }
        };
        fetchLastAnalysis();
    }, [exploitationId, form]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const formattedValues = {
                ...values,
                date_realisation: values.date_realisation.toISOString(),
                exploitation_id: exploitationId,
            };
            await createWaterAnalysis(formattedValues);
            message.success('Analyse d\'eau créée avec succès');
            form.resetFields();
            navigate(`/admin/exploitation/${exploitationId}/analysis/eau`);
        } catch (error) {
            console.error('Erreur lors de la création de l\'analyse d\'eau:', error);
            message.error('Échec de la création de l\'analyse d\'eau');
        } finally {
            setLoading(false);
        }
    };

    const waterParameters = [
        { key: 'cation', name: 'Cations', isHeader: true },
        { key: 'sodium', name: 'Sodium', symbol: 'Na⁺' },
        { key: 'potassium', name: 'Potassium', symbol: 'K⁺' },
        { key: 'magnesium', name: 'Magnésium', symbol: 'Mg²⁺' },
        { key: 'calcium', name: 'Calcium', symbol: 'Ca²⁺' },
        { key: 'ammonium', name: 'Ammonium', symbol: 'NH₄⁺' },
        { key: 'anion', name: 'Anions', isHeader: true },
        { key: 'chlorures', name: 'Chlorures', symbol: 'Cl⁻' },
        { key: 'sulfates', name: 'Sulfates', symbol: 'SO₄²⁻' },
        { key: 'carbonates', name: 'Carbonates', symbol: 'CO₃²⁻' },
        { key: 'bicarbonates', name: 'Bicarbonates', symbol: 'HCO₃⁻' },
        { key: 'nitrates', name: 'Nitrates', symbol: 'NO₃⁻' },
        { key: 'dihydrogenophosphates', name: 'Dihydrogénophosphates', symbol: 'H₂PO₄⁻' },
        { key: 'oligoElements', name: 'Oligo-éléments', isHeader: true },
        { key: 'cuivre', name: 'Cuivre', symbol: 'Cu²⁺' },
        { key: 'zinc', name: 'Zinc', symbol: 'Zn²⁺' },
        { key: 'fer', name: 'Fer', symbol: 'Fe²⁺/Fe³⁺' },
        { key: 'manganese', name: 'Manganèse', symbol: 'Mn²⁺' },
        { key: 'bore', name: 'Bore', symbol: 'B' },
    ];


    const duplicateLastAnalysis = () => {
        if (!lastAnalysis) {
            message.error('Aucune analyse précédente trouvée');
            return;
        }
        const prefillData = { ...lastAnalysis };
        // remove the id and date_realisation and assign the rest of the values to the form
        delete prefillData.id;
        delete prefillData.date_realisation;
        form.setFieldsValue(prefillData);
        message.success('Analyse précédente dupliquée avec succès');
    }

    return (
        <div className="bg-white h-full">
            <Header title="Créer une analyse d'eau" />
            <div className="h-[calc(100%-100px)] p-6">
                <Link
                    to={`/admin/exploitation/${exploitationId}/analysis/eau`}
                    className="cursor-pointer flex pl-5 gap-2 mt-4"
                >
                    <img src={backIcon} alt="" className="w-5" />
                    <div className="font-semibold text-base text-indigo-600">Retour</div>
                </Link>
                {loading && <Loading />}
                <h1 className="text-2xl font-semibold mt-4">Nouvelle Analyse d'Eau</h1>


                <div className='max-w-[1000px] mx-auto'>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-2xl font-semibold">Nouvelle Analyse d'Eau</h2>
                        <Button onClick={duplicateLastAnalysis} type='secondary' className='text-md border-indigo-600 border-2 text-indigo-600 hover:bg-indigo-600 hover:text-white'>
                            Dupliquer la dernière analyse
                        </Button>
                    </div>
                    <Form
                        form={form}
                        name="createWaterAnalysis"
                        onFinish={onFinish}
                        layout="vertical"
                        className='bg-white max-w-[900px] p-6 rounded-lg shadow-md'
                    >
                        <div className="grid grid-cols-2 gap-x-24 mb-4">
                            <Form.Item name="date_realisation" label="Date de réalisation" rules={[{ required: true }]}>
                                <CustomDatePicker />
                            </Form.Item>
                            <Form.Item name="ec" label="EC (µS/cm)">
                                <InputNumber />
                            </Form.Item>
                            <Form.Item name="laboratoire" label="Laboratoire">
                                <Input />
                            </Form.Item>
                            <Form.Item name="ph" label="pH">
                                <InputNumber step={0.1} />
                            </Form.Item>
                            <Form.Item name="echantillon" label="Échantillon">
                                <Input />
                            </Form.Item>
                        </div>
                        <h2 className="text-xl font-semibold mt-6 mb-4">Paramètres de l'eau</h2>
                        <table className="w-full border-collapse border border-gray-300 mb-4">
                            <thead>
                                <tr>
                                    <th className="border border-gray-300 bg-gray-300 px-2 py-2">Paramètre</th>
                                    <th className="border border-gray-300 bg-gray-300 px-2 py-2"></th>
                                    <th className="border border-gray-300 bg-gray-300 px-2 py-2">mg/L</th>
                                    <th className="border border-gray-300 bg-gray-300 px-2 py-2">meq/L</th>
                                    <th className="border border-gray-300 bg-gray-300 px-2 py-2">Limite/Valeur</th>
                                </tr>
                            </thead>
                            <tbody>
                                {waterParameters.map(param => (
                                    <tr key={param.key}>
                                        {param.isHeader ? (
                                            <td colSpan="5" className="border border-gray-300 bg-gray-200 px-2 py-2 text-left font-semibold">{param.name}</td>
                                        ) : (
                                            <>
                                                <td className="border border-gray-300 px-2 py-2">{param.name}</td>
                                                <td className="border border-gray-300 px-2 py-2">{param.symbol}</td>
                                                <td className="border border-gray-300 px-2 py-2">
                                                    <Form.Item name={`${param.key}_mg_l`} noStyle>
                                                        <InputNumber step={0.01} />
                                                    </Form.Item>
                                                </td>
                                                <td className="border border-gray-300 px-2 py-2">
                                                    <Form.Item name={`${param.key}_meq_l`} noStyle>
                                                        <InputNumber step={0.01} />
                                                    </Form.Item>
                                                </td>
                                                <td className="border border-gray-300 px-2 py-2">
                                                    <Form.Item name={`${param.key}_limval`} noStyle>
                                                        <InputNumber step={0.01} />
                                                    </Form.Item>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Créer l'Analyse d'Eau
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default CreateWaterAnalysis;